import { useEffect, type ReactNode } from "react";

/** A form that slides over the right edge: adding a provider, editing one,
 * uploading a price list. The list stays where it was underneath, so the
 * office sees the row it just changed the moment the drawer closes. Escape
 * and a click on the backdrop close it, as a menu would. */
export function AdminDrawer({
  title,
  sub,
  onClose,
  wide = false,
  children,
}: {
  title: string;
  sub?: ReactNode;
  onClose: () => void;
  wide?: boolean;
  children: ReactNode;
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} aria-hidden="true" />
      <aside className={wide ? "drawer wide" : "drawer"} role="dialog" aria-modal="true" aria-label={title}>
        <div className="drawer-head">
          <h2>{title}</h2>
          {sub === undefined ? null : <span className="drawer-sub">{sub}</span>}
          <button type="button" className="row-button drawer-close" onClick={onClose}>
            Close
          </button>
        </div>
        <div className="drawer-body">{children}</div>
      </aside>
    </>
  );
}

/** A search box over a list that is already on screen: it filters what was
 * fetched rather than asking the server again. */
export function ListSearch({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div className="toolbar">
      <input
        type="search"
        className="search-input admin-search"
        placeholder={placeholder}
        aria-label={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** True when every word typed appears somewhere in `fields`. */
export function matches(query: string, fields: readonly (string | null | undefined)[]): boolean {
  const words = query.trim().toLowerCase().split(/\s+/).filter((w) => w !== "");
  if (words.length === 0) {
    return true;
  }
  const haystack = fields.filter((f) => f !== null && f !== undefined).join(" ").toLowerCase();
  return words.every((word) => haystack.includes(word));
}
